import { Dialog, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { Stack } from '@mui/system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { logger } from '@common/logging/logger';
import { TextInput } from '@common/components/inputs';
import { Theme } from '@modules/common/types/general';
import Label from '@/modules/common/components/Label';
import { useNavigation } from '@/modules/common/hooks';
import { FILENAME_FORBIDDEN_CHARACTERS } from '@/modules/common/helpers/file';
import { UserPreferenceName } from '@modules/userPreferences';
import { useUserPreference } from '@modules/userPreferences/hooks';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';

import { useFloorplanAdminStore } from './store';

type Props = {
  isOpen: boolean;
  onCloseClick: () => void;
};

export function FloorplanDuplicator({ isOpen, onCloseClick }: Props) {
  const { t } = useTranslation(['interface', 'common']);
  const { goToBuilder } = useNavigation();
  const buttonRef = useRef(null);
  const floorplanIdBeingDuplicated = useFloorplanAdminStore((state) => state.floorplanIdBeingDuplicatedState);
  const [textInput, setTextInput] = useState('');
  const theme = useUserPreference(UserPreferenceName.GENERAL_THEME);
  const { projectId } = useParams();
  
  useEffect(() => {
    if (floorplanIdBeingDuplicated) {
      const floorplanBeingDuplicated = useFloorplanAdminStore
        .getState()
        .floorPlanGroups.find((item) => item.alternativeId === floorplanIdBeingDuplicated);

      if (!floorplanBeingDuplicated) {
        logger.error(
          'could not find the alternative being duplicated among the loaded alternatives',
        );
        return;
      }
      // plz fix me
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTextInput(`${floorplanBeingDuplicated.name} - Copy`);
    }
  }, [floorplanIdBeingDuplicated]);

  const handleClose = useCallback(() => {
    onCloseClick();
    useFloorplanAdminStore.getState().setFloorplanIdBeingDuplicatedState(null);
  }, [onCloseClick]);

  const onDuplicateClick = useCallback(async () => {
    const selectedAternativeId = useFloorplanAdminStore.getState().floorplanIdBeingDuplicatedState;

    if (!selectedAternativeId) {
      logger.error('Expected to floorplanIdBeingDuplicated be defined');
      return;
    }

    if (!projectId) {
      logger.error('Expected projectId to be defined');
      return;
    }

    try {
      const duplicated = await ProjectServiceConnector.getInstance().duplicateLayoutAlternative({
        projectId,
        alternativeId: selectedAternativeId,
        newAlternativeName: textInput,
      });

      goToBuilder(duplicated.alternativeId, duplicated.lastRevisionId);
    } catch (e) {
      throw new Error('Error', e);
    }
  }, [projectId, textInput, goToBuilder]);

  const handleTextInputChange = useCallback((value) => {
    setTextInput(value);
  }, []);

  useEffect(
    () => () => {
      handleClose();
    },
    [handleClose],
  );

  return (
    <Dialog onClose={handleClose} open={isOpen} className={theme === Theme.DARK ? 'dark' : ''}>
      <Stack gap={2} p={2}>
        <Typography variant='h6' align='center'>
          {t('interface:home.duplicate_floorplan', 'Duplicate floorplan')}
        </Typography>
        <Label label={t('interface:home.name', 'Name')}>
          <TextInput
            value={textInput}
            onChange={handleTextInputChange}
            forbiddenCharacters={FILENAME_FORBIDDEN_CHARACTERS}
          />
        </Label>
        <Stack gap={1}>
          <Button
            ref={buttonRef}
            variant='secondary'
            onClick={onDuplicateClick}
            disabled={textInput.length <= 0}
          >
            {t('interface:home.duplicate_button', 'Duplicate Floorplan')}
          </Button>
          <Button onClick={handleClose}>{t('common:cancel', 'Cancel')}</Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
